import React from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { Switch, Route, BrowserRouter } from 'react-router-dom';
import { apiPost } from './services';
import posthog from 'posthog-js';

import Azure from './views/azure';

import Empty from './views/layouts/empty';
import Main from './views/layouts/main';
import Zero from './views/layouts/zero';

import Home from './views/home';

import Accelerate from './views/accelerate';
import Admin from './views/admin';
import AdminBusinessUnits from './views/admin/businessUnits';
import AdminProfileGroups from './views/admin/profileGroups';
import AdminCompetencyAreas from './views/admin/competencyAreas';
import AdminCompetencies from './views/admin/competencies';
import AdminCompetencyRatingGroups from 'views/admin/competencyRatingGroups';
import AdminDepartments from './views/admin/departments';
import AdminGoals from './views/admin/goals';
import AdminCollections from './views/admin/collections';
import AdminLanguages from './views/admin/languages';
import AdminLocations from './views/admin/locations';
import AdminMetrics from './views/admin/metrics';
import AdminPathways from './views/admin/pathways';
import AdminPositiveIndicators from './views/admin/positiveIndicators';
import AdminRoles from './views/admin/roles';
import AdminUsers from './views/admin/users';
import AdminUserBulk from './views/admin/usersBulk';
import AdminTags from './views/admin/tags';
import AdminSettings from './views/admin/settings';
import AdminIntegrations from './views/admin/integrations';
import OAuthHubspot from './views/admin/oauth/hubspot';
import OAuthOutreach from './views/admin/oauth/outreach';
import OAuthShowpad from './views/admin/oauth/showpad';
import Behaviour from './views/behaviour';
import Blog from './views/blog';
import BlogPost from './views/blogPost';
import Join from './views/join';
import KnowledgeBank from './views/knowledgeBank';
import LearnTracker from './views/user/learnTracker';
import Loading from './views/loading';
import Login from './views/login';
import MyDashboard from './views/myDashboard';
import MyTeam from './views/myTeam';
import OneDrive from './views/azure/onedrive';
import Partner from './views/partner';
import Password from './views/password';
import PasswordReset from './views/passwordReset';
import PathwayTracker from './views/user/pathwayTracker';
import Pitchback from './views/pitchback';
import ReviewTracker from './views/user/reviewTracker';
import Salesforce from './views/salesforce';
import SalesforceCallback from './views/salesforce/callback';
import SalesforceFields from './views/admin/integrations/components/salesforce';
import Saml from './views/saml';
import Search from './views/search';
import Spike from './views/spike';
import Spoofing from './views/spoofing';
import SSOLogout from './views/ssoLogout';
import Support from './views/support';
import TaskTracker from './views/user/tracker';
import Trackers from './views/trackers';
import User121s from './views/user/1-2-1s';
import UserAppraisals from './views/user/appraisals';
import UserLearning from './views/user/learning';
import UserPathways from './views/user/pathways';
import UserHandle from './views/user/handle';
import UserTask from './views/user/task';
import UserTasks from './views/user/tasks';
import Welcome from './views/welcome';
import ZeroReview from './views/zero/review';
import SlackIntegration from './integrate/slack';
import { ComingSoon, Error, Forbidden, NotFound, ReduxHelpers } from 'e4shared';

import config from './config';
import reducer from './reducers';

import axe from '@axe-core/react';

import './index.css';
import { getStorage, setStorage } from 'helpers/storage';

export const registerPageView = (guid, spoofing, props) => {
    if (!spoofing) {
        const url = props.location.pathname + props.location.search;
        if (url && guid && url.length <= 150) {
            apiPost('view', { url }, guid);
        }
    }

    return false;
};

export const RouteWithLayout = ({ layout, component, ...rest }) => {
    React.useEffect(() => {
        if (config.environment !== 'live' && config.useAxe) {
            axe(React, ReactDOM, 1000);
        }
    }, [component]);

    const spoofing = useSelector(state => state.user.user?.spoofing);

    return (
        <Route
            key={Date.now()}
            {...rest}
            render={props => {
                const guid = getStorage('user');
                guid && setStorage('user', guid);

                const tempGuid = getStorage('tempUser');
                tempGuid && setStorage('tempUser', tempGuid);

                if (!guid && !tempGuid) {
                    props = { ...props, invalidUser: true };
                }

                registerPageView(guid, spoofing, props);

                return React.createElement(layout, props, React.createElement(component, props));
            }}
        />
    );
};

export const App = () => {
    const store = ReduxHelpers.configureStore(undefined, reducer, config);
    posthog.init(config.posthog.key, { api_host: 'https://eu.posthog.com' });

    return (
        <Provider store={store}>
            <BrowserRouter>
                <Switch>
                    <RouteWithLayout layout={Main} path="/" exact component={Home} />
                    <RouteWithLayout layout={Main} path="/welcome/:id?" exact component={Welcome} />
                    {/* Main Pages */}
                    <RouteWithLayout layout={Main} path="/my-team" component={MyTeam} />
                    <RouteWithLayout layout={Main} path="/my-dashboard" component={MyDashboard} />
                    <RouteWithLayout layout={Main} path="/insights" component={MyDashboard} />
                    <RouteWithLayout layout={Main} path="/behavior/:id?" component={Behaviour} />
                    <RouteWithLayout layout={Main} path="/behaviour/:id?" component={Behaviour} />
                    <RouteWithLayout layout={Main} path="/dashboard" component={MyDashboard} />
                    <RouteWithLayout layout={Main} path="/objectives/:id/objective/:taskId" component={UserTask} />
                    <RouteWithLayout layout={Main} path="/objectives/:id?" component={UserTasks} />
                    <RouteWithLayout layout={Main} path="/coaching/appraisals/:id?" component={UserAppraisals} />
                    <RouteWithLayout layout={Main} path="/coaching/1-2-1s/:id?" component={User121s} />
                    <RouteWithLayout layout={Main} path="/coaching/pathways/:id?" component={UserPathways} />
                    <RouteWithLayout layout={Main} path="/develop/reviews/:id?" component={UserAppraisals} />
                    <RouteWithLayout layout={Main} path="/develop/learning/:id?" component={UserLearning} />
                    <RouteWithLayout layout={Main} path="/develop/performance-dashboard/:id?" component={User121s} />
                    <RouteWithLayout layout={Main} path="/develop/pathways/:id?" component={UserPathways} />
                    <RouteWithLayout layout={Main} path="/learn-tracker" component={LearnTracker} />
                    <RouteWithLayout layout={Main} path="/pathway-tracker" component={PathwayTracker} />
                    <RouteWithLayout layout={Main} path="/spike" component={Spike} />
                    <RouteWithLayout layout={Main} path="/spoofing" component={Spoofing} />
                    <RouteWithLayout layout={Main} path="/team/:id?" component={MyDashboard} />
                    <RouteWithLayout layout={Main} path="/blog/post/:post" component={BlogPost} />
                    <RouteWithLayout layout={Main} path="/blog-share/:guid" component={BlogPost} />
                    <RouteWithLayout layout={Main} path="/blog/:id?" exact component={Blog} />
                    <RouteWithLayout layout={Main} path="/blogs" component={Blog} />
                    <RouteWithLayout layout={Main} path="/knowledge-bank/:id?" exact component={KnowledgeBank} />
                    <RouteWithLayout layout={Main} path="/review-tracker" component={ReviewTracker} />
                    <RouteWithLayout layout={Main} path="/salesforce-fields" component={SalesforceFields} />
                    <RouteWithLayout layout={Main} path="/support" component={Support} />
                    <RouteWithLayout layout={Main} path="/organisation" component={Trackers} />
                    <RouteWithLayout layout={Main} path="/organization" component={Trackers} />
                    <RouteWithLayout layout={Main} path="/tracker" component={TaskTracker} />
                    <RouteWithLayout layout={Main} path="/objective-tracker" component={TaskTracker} />
                    <RouteWithLayout layout={Main} path="/admin/business-units" component={AdminBusinessUnits} />
                    <RouteWithLayout layout={Main} path="/admin/competencies" component={AdminCompetencies} />
                    <RouteWithLayout
                        layout={Main}
                        path="/admin/competency-rating-groups"
                        component={AdminCompetencyRatingGroups}
                    />
                    <RouteWithLayout layout={Main} path="/admin/user-groups" component={AdminProfileGroups} />
                    <RouteWithLayout layout={Main} path="/admin/competency-areas" component={AdminCompetencyAreas} />
                    <RouteWithLayout layout={Main} path="/admin/departments" component={AdminDepartments} />
                    <RouteWithLayout layout={Main} path="/admin/languages" component={AdminLanguages} />
                    <RouteWithLayout layout={Main} path="/admin/locations" component={AdminLocations} />
                    <RouteWithLayout layout={Main} path="/admin/metrics" component={AdminMetrics} />
                    <RouteWithLayout layout={Main} path="/admin/pathways" component={AdminPathways} />
                    <RouteWithLayout layout={Main} path="/admin/goals" component={AdminGoals} />
                    <RouteWithLayout layout={Main} path="/admin/collections" component={AdminCollections} />
                    <RouteWithLayout layout={Main} path="/admin/positive-indicators" component={AdminPositiveIndicators} />
                    <RouteWithLayout layout={Main} path="/admin/roles" component={AdminRoles} />
                    <RouteWithLayout layout={Main} path="/admin/tags" component={AdminTags} />
                    <RouteWithLayout layout={Main} path="/admin/users" component={AdminUsers} />
                    <RouteWithLayout layout={Main} path="/admin/users-bulk" component={AdminUserBulk} />
                    <RouteWithLayout layout={Main} path="/admin/config" component={AdminSettings} />
                    <RouteWithLayout layout={Main} path="/admin/integrations" component={AdminIntegrations} />
                    <RouteWithLayout layout={Main} path="/admin" component={Admin} />
                    <RouteWithLayout layout={Main} path="/oauth/hubspot" component={OAuthHubspot} />
                    <RouteWithLayout layout={Main} path="/oauth/outreach" component={OAuthOutreach} />
                    <RouteWithLayout layout={Main} path="/oauth/showpad" component={OAuthShowpad} />
                    <RouteWithLayout layout={Main} path="/handle/:handle" component={UserHandle} />
                    <RouteWithLayout layout={Main} path="/search/:search?" component={Search} />
                    <RouteWithLayout layout={Main} path="/pitchback" component={Pitchback} />
                    <RouteWithLayout layout={Main} path="/tag/:tag" component={Search} />
                    {/* Login / Special Pages*/}
                    <RouteWithLayout layout={Empty} path="/login" component={Login} />
                    <RouteWithLayout layout={Empty} path="/join/:guid" component={Join} />
                    <RouteWithLayout layout={Empty} path="/partner/:guid" component={Partner} />
                    <RouteWithLayout layout={Empty} path="/salesforce" component={Salesforce} />
                    <RouteWithLayout layout={Main} path="/oauth/salesforce/callback" component={SalesforceCallback} />
                    <RouteWithLayout layout={Empty} path="/accelerate" exact component={Accelerate} />
                    <RouteWithLayout layout={Empty} path="/azure" exact component={Azure} />
                    <RouteWithLayout layout={Empty} path="/onedrive" component={OneDrive} />
                    <RouteWithLayout layout={Empty} path="/saml/:guid" exact component={Saml} />
                    <RouteWithLayout layout={Empty} path="/forgot-password" component={Password} />
                    <RouteWithLayout layout={Empty} path="/reset-password" component={PasswordReset} />
                    <RouteWithLayout layout={Empty} path="/error" component={Error} />
                    <RouteWithLayout layout={Empty} path="/loading" component={Loading} />
                    <RouteWithLayout layout={Empty} path="/forbidden" component={Forbidden} />
                    <RouteWithLayout layout={Empty} path="/sso-logout" component={SSOLogout} />
                    <RouteWithLayout layout={Empty} path="/coming-soon" component={ComingSoon} />
                    {/* Zero Pages*/}
                    <RouteWithLayout layout={Zero} path="/zero-review/:guid" component={ZeroReview} />
                    <RouteWithLayout layout={Zero} path="/zero-staff-review/:guid" component={ZeroReview} />

                    {/* Integrations*/}
                    <RouteWithLayout layout={Main} path="/integrate/slack" component={SlackIntegration} />

                    {/* 404 Page*/}
                    <RouteWithLayout layout={Empty} path="/*" component={NotFound} />
                </Switch>
            </BrowserRouter>
        </Provider>
    );
};

export default App;
